import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Dialog,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { ApiStatus } from '../../utils/api.types';
import { IStatutoryPaymentForm } from '../../utils/statutorySettlement.types';

interface StatutoryPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  paymentForm: IStatutoryPaymentForm;
  onPaymentFormChange: (field: string, value: any) => void;
  onSubmit: () => void;
  paymentStatus: ApiStatus;
  bankLedgers: any[];
}

const TYPE_CONFIG: Record<string, { title: string; chipLabel: string; dueDateNote: string; portalNote: string; lateNote: string; penaltyNote: string }> = {
  PF: {
    title: 'Record PF Payment to EPFO',
    chipLabel: 'PF Payment Details',
    dueDateNote: 'PF payment due by 15th of next month via ECR on EPFO portal',
    portalNote: 'File ECR (Electronic Challan cum Return) on unifiedportal-emp.epfindia.gov.in',
    lateNote: 'Late deposit attracts 12% p.a. interest + damages under Section 14B',
    penaltyNote: 'Non-compliance can lead to prosecution under EPF Act'
  },
  ESI: {
    title: 'Record ESI Payment to ESIC',
    chipLabel: 'ESI Payment Details',
    dueDateNote: 'ESI payment due by 15th of next month via ESIC portal',
    portalNote: 'File contribution on esic.gov.in portal',
    lateNote: 'Late deposit attracts 12% p.a. interest',
    penaltyNote: 'Non-compliance attracts penalty under ESI Act Section 85'
  },
  PROFESSIONAL_TAX: {
    title: 'Record Professional Tax Payment',
    chipLabel: 'Professional Tax Payment Details',
    dueDateNote: 'Professional Tax due dates vary by state (typically monthly or half-yearly)',
    portalNote: 'Payment via respective State Government portal',
    lateNote: 'Late payment attracts interest as per state rules',
    penaltyNote: 'Check your state-specific PT rules for penalty details'
  }
};

const StatutoryPaymentDialog: React.FC<StatutoryPaymentDialogProps> = ({
  open,
  onClose,
  paymentForm,
  onPaymentFormChange,
  onSubmit,
  paymentStatus,
  bankLedgers
}) => {
  const config = TYPE_CONFIG[paymentForm.type] || TYPE_CONFIG.PF;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box>
        {/* Header */}
        <Grid container>
          <Grid item xs={12} p={2} pl={2} bgcolor='#e9ecf1' style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }}>
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={
                  paymentStatus === ApiStatus.LOADING ||
                  !paymentForm.bankAccount ||
                  paymentForm.amount <= 0
                }
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                size='small'
              >
                {paymentStatus === ApiStatus.LOADING ? <CircularProgress size={20} /> : 'Record Payment'}
              </Button>
              <Button
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>{config.title}</h3>
          </Grid>
        </Grid>

        {/* Form Content */}
        <Grid container p={2} spacing={2}>
          {/* Payment Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label={config.chipLabel} size="small" />
            </Divider>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Amount*</h5>
            <TextField
              placeholder='Payment Amount'
              className='c-text-field-4'
              type="number"
              value={paymentForm.amount}
              onChange={(e) => onPaymentFormChange('amount', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Date*</h5>
            <TextField
              placeholder='Payment Date'
              className='c-text-field-4'
              type="date"
              value={paymentForm.paymentDate}
              onChange={(e) => onPaymentFormChange('paymentDate', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Challan Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="Challan / Reference Details" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>
              {paymentForm.type === 'PF' ? 'ECR/TRRN Number' : 'Challan Number'}
            </h5>
            <TextField
              placeholder={paymentForm.type === 'PF' ? 'Enter ECR/TRRN' : 'Enter Challan Number'}
              className='c-text-field-4'
              value={paymentForm.challanNumber || ''}
              onChange={(e) => onPaymentFormChange('challanNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Reference Number</h5>
            <TextField
              placeholder='Transaction Reference'
              className='c-text-field-4'
              value={paymentForm.referenceNumber || ''}
              onChange={(e) => onPaymentFormChange('referenceNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          {/* Bank & Payment Mode */}
          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Bank Account*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.bankAccount}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('bankAccount', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px',
                    maxHeight: 300
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {bankLedgers.length === 0 ? (
                <MenuItem disabled value="">
                  No bank accounts found
                </MenuItem>
              ) : (
                bankLedgers.map((bank: any) => (
                  <MenuItem key={bank._id} value={bank._id}>
                    {bank.ledgerName || bank.accountName}
                  </MenuItem>
                ))
              )}
            </Select>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Mode*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.paymentMode}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('paymentMode', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="NEFT">NEFT</MenuItem>
              <MenuItem value="RTGS">RTGS</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="NETBANKING">Net Banking</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={12}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Remarks (Optional)</h5>
            <TextField
              placeholder='Remarks'
              className='c-text-field-4'
              multiline
              rows={3}
              value={paymentForm.remarks || ''}
              onChange={(e) => onPaymentFormChange('remarks', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          {/* Compliance Notes */}
          <Grid item xs={12} mt={2}>
            <Box sx={{ bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2" fontWeight="600" color="#856404">
                Important Compliance Notes:
              </Typography>
              <Typography variant="caption" display="block" color="#856404" mt={0.5}>
                {config.dueDateNote}
              </Typography>
              <Typography variant="caption" display="block" color="#856404">
                {config.portalNote}
              </Typography>
              <Typography variant="caption" display="block" color="#856404">
                {config.lateNote}
              </Typography>
              <Typography variant="caption" display="block" color="#856404">
                {config.penaltyNote}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default StatutoryPaymentDialog;
